import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { getErrorMessage } from '../types/api-error';
import type { DailyReportStat } from '../types/admin';

interface StoreDayStat {
  date: string;
  count: number;
}

interface StoreGroup {
  storeName: string;
  stats: StoreDayStat[];
}

function formatDate(value: string | Date): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function StatisticsPage() {
  const [stats, setStats] = useState<DailyReportStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStatistics = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<DailyReportStat[]>('/reports/statistics');
      setStats(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getErrorMessage(err, '加载统计数据失败'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  const grouped = useMemo<StoreGroup[]>(() => {
    const map = new Map<string, StoreDayStat[]>();
    for (const s of stats) {
      const list = map.get(s.storeName) || [];
      list.push({ date: s.date, count: s.count });
      map.set(s.storeName, list);
    }
    return Array.from(map.entries()).map(([storeName, stats]) => ({
      storeName,
      stats,
    }));
  }, [stats]);

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">数据统计</h2>
        <p className="text-sm text-gray-500 mt-1">各门店每日已处理报告数量</p>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">加载中…</div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-10 text-gray-500">暂无数据</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {grouped.map((g) => (
            <div
              key={g.storeName}
              className="bg-white rounded-xl shadow p-4 flex flex-col"
            >
              <h3 className="text-base font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100">
                {g.storeName}
              </h3>
              <div className="space-y-2 flex-1">
                {g.stats.map((s) => (
                  <div
                    key={s.date}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-gray-600">{formatDate(s.date)}</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                      {s.count} 份
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
                <span className="text-gray-500">合计</span>
                <span className="font-semibold text-gray-900">
                  {g.stats.reduce((sum, s) => sum + s.count, 0)} 份
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
